import type {
  ApplicationMappingView,
  GlobalFilters,
  MappingEdge,
  MappingIssue,
  MappingNode,
  ProcessMappingView,
  ProcessTreeNode,
  QueryResult,
} from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import { ratio, round } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, canEdit, resolved } from '../context';
import { applyFilters } from '../filters';
import { buildMetadata } from '../metadata';
import { simpleRef } from '../refs';

/**
 * Process-to-product mapping (ref: 05_…Mapping.png).
 *
 * The three columns are projected from the same relationship records the rest
 * of the platform uses, and each edge carries its own status, confidence and
 * evidence. An edge that looks different on the canvas and in the issue list is
 * the failure mode this shape is designed to prevent.
 */

export function createMappingRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  return {
    async getProcessMapping(
      filters: GlobalFilters,
      processRootId?: string,
    ): Promise<QueryResult<ProcessMappingView>> {
      const scope = applyFilters(dataset, index, user, filters);

      const childCount = new Map<string, number>();
      for (const process of dataset.processes) {
        if (!process.parentCode) continue;
        childCount.set(process.parentCode, (childCount.get(process.parentCode) ?? 0) + 1);
      }

      const mappedCountByProcess = new Map<string, number>();
      for (const link of dataset.productProcessLinks) {
        if (!scope.productCodes.has(link.productCode)) continue;
        mappedCountByProcess.set(
          link.processCode,
          (mappedCountByProcess.get(link.processCode) ?? 0) + 1,
        );
      }

      const processTree: ProcessTreeNode[] = dataset.processes
        .filter(
          (process) => process.level === 'L1' || process.level === 'L2' || process.level === 'L3',
        )
        .map((process) => ({
          id: process.id,
          businessCode: process.businessCode,
          name: process.name,
          level: process.level,
          parentId: process.parentCode
            ? (dataset.processes.find((p) => p.businessCode === process.parentCode)?.id ?? null)
            : null,
          childCount: childCount.get(process.businessCode) ?? 0,
          mappedCount: mappedCountByProcess.get(process.businessCode) ?? 0,
          hasChildren: (childCount.get(process.businessCode) ?? 0) > 0,
        }));

      /*
        No selection, the demo default, and an explicit selection are three
        different things.

        `undefined` means the reader has just arrived, and opens on the crew
        recovery L2 — the north-star demo path. `'all'` means they pressed "All
        processes in scope" and wants the catalogue unbounded; that used to fall
        through to the same default, so the one control offering to widen the
        page quietly re-applied the narrowest selection on it.
      */
      const selectedProcess =
        processRootId === 'all'
          ? undefined
          : ((processRootId
              ? dataset.processes.find(
                  (p) => p.id === processRootId || p.businessCode === processRootId,
                )
              : undefined) ?? dataset.processes.find((p) => p.businessCode === 'PRC-5.4'));

      /*
        The selection is a subtree, at every depth beneath the node.

        This used to collect the selected process and its *direct children* only.
        The catalogue is four levels deep and products are mapped at L3, so
        selecting anything at L1 gathered a set of L2 headings that no product
        link mentions — the selection bounded to nothing, and the canvas fell
        back to drawing the unbounded graph. Clicking the top of the tree
        therefore looked exactly like clicking nothing.
      */
      const childrenByParentCode = new Map<string, string[]>();
      for (const process of dataset.processes) {
        if (!process.parentCode) continue;
        const siblings = childrenByParentCode.get(process.parentCode);
        if (siblings) siblings.push(process.businessCode);
        else childrenByParentCode.set(process.parentCode, [process.businessCode]);
      }

      const selectedCodes = new Set<string>();
      if (selectedProcess) {
        const pending = [selectedProcess.businessCode];
        while (pending.length > 0) {
          const code = pending.pop();
          if (code === undefined || selectedCodes.has(code)) continue;
          selectedCodes.add(code);
          pending.push(...(childrenByParentCode.get(code) ?? []));
        }
      }

      /*
        Which processes every figure on this page is about.

        A selection bounds the page; "All processes in scope" is the whole
        catalogue. Both the canvas and the cards above it read this one set,
        because a card that counts a different population from the graph beneath
        it is the failure this screen was built to prevent — and the card that
        says "the bounded selection" was, until now, counting the estate.
      */
      const inSelection = (processCode: string): boolean =>
        selectedCodes.size === 0 || selectedCodes.has(processCode);

      const relevantProcessLinks = dataset.productProcessLinks.filter(
        (link) => inSelection(link.processCode) && scope.productCodes.has(link.productCode),
      );
      const productCodes = new Set(relevantProcessLinks.map((link) => link.productCode));
      const relevantAppLinks = dataset.productApplicationLinks.filter((link) =>
        productCodes.has(link.productCode),
      );

      const nodes: MappingNode[] = [];
      const seenNodes = new Set<string>();
      const pushNode = (node: MappingNode): void => {
        if (seenNodes.has(node.id)) return;
        seenNodes.add(node.id);
        nodes.push(node);
      };

      /*
        Which processes the canvas draws its first column from.

        A selection draws its own subtree, including the processes in it that
        nothing is mapped to — those are the gaps the reader came for. With no
        selection there is no subtree to enumerate, so the column is the
        processes that actually carry a relationship; drawing all 225 would be a
        catalogue, not a canvas, and `boundGraph` would cut it to the budget
        anyway. Reading `selectedCodes` in both cases left the unbounded view
        with an empty first column, and an empty first column empties the two
        columns that hang off it.
      */
      const canvasProcessCodes =
        selectedCodes.size > 0
          ? selectedCodes
          : new Set(relevantProcessLinks.map((link) => link.processCode));

      for (const code of canvasProcessCodes) {
        const process = dataset.processes.find((p) => p.businessCode === code);
        if (!process) continue;
        pushNode({
          id: process.id,
          column: 'process',
          entityType: 'Process',
          businessCode: process.businessCode,
          label: process.name,
          sublabel: process.level,
          iconToken: 'flow',
          confidenceScore: round(process.provenance.confidence * 100, 0),
          fitLabel: null,
          ownerLabel: null,
          isSelected: process.businessCode === selectedProcess?.businessCode,
        });
      }

      for (const code of productCodes) {
        const product = index.productsByCode.get(code);
        if (!product) continue;
        const owner = index.peopleByCode.get(product.ownerPersonCode);
        pushNode({
          id: product.id,
          column: 'product',
          entityType: 'Product',
          businessCode: product.businessCode,
          label: product.name,
          sublabel: product.strategicTierLabel,
          iconToken: 'cube',
          confidenceScore: round(product.provenance.confidence * 100, 0),
          fitLabel: product.health === 'Healthy' ? 'Healthy' : 'Needs attention',
          ownerLabel: owner?.displayName ?? null,
          isSelected: false,
        });
      }

      for (const link of relevantAppLinks) {
        const application = index.applicationsByCode.get(link.applicationCode);
        if (!application) continue;
        pushNode({
          id: application.id,
          column: 'application',
          entityType: 'Application',
          businessCode: application.businessCode,
          label: application.name,
          sublabel: application.businessCapability,
          iconToken: 'apps',
          confidenceScore: round(application.provenance.confidence * 100, 0),
          fitLabel: application.isDuplicateCandidate ? 'Duplicate candidate' : null,
          ownerLabel: index.peopleByCode.get(application.ownerPersonCode)?.displayName ?? null,
          isSelected: false,
        });
      }

      const edges: MappingEdge[] = [];
      const pushEdge = (
        id: string,
        fromNodeId: string | undefined,
        toNodeId: string | undefined,
        relationshipType: string,
        link: {
          role: MappingEdge['role'];
          status: MappingEdge['status'];
          confidence: number;
          confidenceBand: MappingEdge['confidenceBand'];
          confidenceMethod: MappingEdge['confidenceMethod'];
          evidenceCode: string | null;
          validFrom: string;
          validTo: string | null;
          isDraft: boolean;
        },
        allocationPercent: number | null,
      ): void => {
        if (!fromNodeId || !toNodeId) return;
        edges.push({
          id,
          fromNodeId,
          toNodeId,
          relationshipType,
          role: link.role,
          status: link.status,
          confidenceScore: link.confidence,
          confidenceBand: link.confidenceBand,
          confidenceMethod: link.confidenceMethod,
          allocationPercent,
          evidenceId: link.evidenceCode
            ? (dataset.evidence.find((e) => e.businessCode === link.evidenceCode)?.id ?? null)
            : null,
          validFrom: link.validFrom,
          validTo: link.validTo,
          isDraft: link.isDraft,
          // Never colour or dash alone: the label carries the same meaning.
          styleLabel: `${link.status} · ${link.confidenceBand ?? 'Unknown'} confidence`,
        });
      };

      for (const link of relevantProcessLinks) {
        pushEdge(
          link.id,
          dataset.processes.find((p) => p.businessCode === link.processCode)?.id,
          index.productsByCode.get(link.productCode)?.id,
          'ProcessSupportedByProduct',
          link,
          null,
        );
      }
      for (const link of relevantAppLinks) {
        pushEdge(
          link.id,
          index.productsByCode.get(link.productCode)?.id,
          index.applicationsByCode.get(link.applicationCode)?.id,
          'ProductSupportedByApplication',
          link,
          link.allocationPercent,
        );
      }

      // ── Quality ─────────────────────────────────────────────────────────────
      // The same population the canvas draws. These were computed estate-wide
      // while the strip above them was labelled "the bounded selection", so
      // every figure on the page held still no matter what the reader selected.
      const allProcessLinks = relevantProcessLinks;
      const mappedProcessCodes = new Set(allProcessLinks.map((l) => l.processCode));
      const l3Processes = dataset.processes.filter(
        (p) => p.level === 'L3' && inSelection(p.businessCode),
      );
      const unmapped = l3Processes.filter((p) => !mappedProcessCodes.has(p.businessCode));

      const bandOf = (confidence: number): 'High' | 'Medium' | 'Low' =>
        confidence >= 0.85 ? 'High' : confidence >= 0.65 ? 'Medium' : 'Low';
      const bandCounts = { High: 0, Medium: 0, Low: 0, Unmapped: unmapped.length };
      for (const link of allProcessLinks) bandCounts[bandOf(link.confidence)] += 1;
      const bandTotal = Math.max(
        1,
        bandCounts.High + bandCounts.Medium + bandCounts.Low + bandCounts.Unmapped,
      );

      const averageConfidence =
        allProcessLinks.length === 0
          ? 0
          : allProcessLinks.reduce((sum, l) => sum + l.confidence, 0) / allProcessLinks.length;

      // Overlaps: an L3 process claimed as Primary by more than one product.
      const primaryByProcess = new Map<string, string[]>();
      for (const link of allProcessLinks) {
        if (link.role !== 'Primary') continue;
        const bucket = primaryByProcess.get(link.processCode) ?? [];
        bucket.push(link.productCode);
        primaryByProcess.set(link.processCode, bucket);
      }
      const overlaps = Array.from(primaryByProcess.entries()).filter(([, list]) => list.length > 1);

      /*
        Conflicts are recorded against an entity, not a process, so they narrow
        by every entity the selection reaches — the products mapped to it and
        the applications behind those products.

        Narrowing by products alone left the counter reading 0 for every
        selection, because the open conflicts are held against applications. A
        counter that is structurally always zero is worse than an absent one:
        it reads as "no conflicts here" rather than "this was never asked".
      */
      const reachedEntityCodes = new Set<string>([
        ...productCodes,
        ...relevantAppLinks.map((link) => link.applicationCode),
        ...selectedCodes,
      ]);
      const conflicts = dataset.sourceConflicts.filter(
        (c) =>
          c.status === 'Open' && (selectedCodes.size === 0 || reachedEntityCodes.has(c.entityCode)),
      );

      const issues: MappingIssue[] = [
        ...unmapped.slice(0, 8).map((process) => ({
          id: `issue-unmapped-${process.businessCode}`,
          kind: 'unmapped' as const,
          severity: 'Warning' as const,
          title: `${process.name} has no product`,
          detail: 'No approved product relationship exists for this level 3 process.',
          subjectRefs: [simpleRef(process.id, 'Process', process.businessCode, process.name)],
          hint: 'Propose a relationship from the product that owns the outcome.',
        })),
        ...overlaps.slice(0, 6).map(([processCode, productCodesList]) => {
          const process = dataset.processes.find((p) => p.businessCode === processCode);
          return {
            id: `issue-overlap-${processCode}`,
            kind: 'overlap' as const,
            severity: 'Error' as const,
            title: `${process?.name ?? processCode} is claimed by ${productCodesList.length} products`,
            detail: 'More than one product holds a Primary relationship to this process.',
            subjectRefs: productCodesList
              .map((code) => index.productsByCode.get(code))
              .filter((p): p is NonNullable<typeof p> => p !== undefined)
              .map((p) => simpleRef(p.id, 'Product', p.businessCode, p.name)),
            hint: 'Agree a single owning product; the others become Supporting.',
          };
        }),
        ...conflicts.map((conflict) => ({
          id: `issue-conflict-${conflict.businessCode}`,
          kind: 'conflict' as const,
          severity: 'Error' as const,
          title: `${conflict.entityCode} — ${conflict.field} disagrees between sources`,
          detail: conflict.claims
            .map((claim) => `${claim.sourceSystem}: ${claim.value}`)
            .join(' · '),
          subjectRefs: [
            simpleRef(conflict.id, conflict.entityType, conflict.entityCode, conflict.entityCode),
          ],
          hint: 'A steward must choose the surviving value and record why.',
        })),
      ];

      const publishedSet = dataset.mappingSets.find((set) => set.status === 'Published');
      const draftSet = dataset.mappingSets.find((set) => set.status === 'Draft');
      const activeSet = canEdit(user) && draftSet ? draftSet : (publishedSet ?? draftSet);

      const view: ProcessMappingView = {
        processTree,
        selectedProcessId: selectedProcess?.id ?? null,
        mappingSet: {
          id: activeSet?.id ?? '',
          name: activeSet?.name ?? 'Process-to-Product Mapping',
          version: activeSet?.version ?? 1,
          status: activeSet?.status ?? 'Draft',
          lastEditedAt: activeSet?.lastEditedAt ?? null,
          isEditable: activeSet?.status === 'Draft' && canEdit(user),
        },
        nodes,
        edges,
        quality: {
          overallPercent: round((1 - bandCounts.Unmapped / bandTotal) * 100, 1),
          bands: (['High', 'Medium', 'Low', 'Unmapped'] as const).map((band) => ({
            band,
            count: bandCounts[band],
            percent: ratio(bandCounts[band], bandTotal),
          })),
          dimensions: [
            {
              dimension: 'Coverage',
              percent: ratio(mappedProcessCodes.size, Math.max(1, l3Processes.length)),
            },
            {
              dimension: 'Evidence',
              percent: ratio(
                allProcessLinks.filter((l) => l.evidenceCode !== null).length,
                Math.max(1, allProcessLinks.length),
              ),
            },
            {
              dimension: 'Approval',
              percent: ratio(
                allProcessLinks.filter((l) => l.status === 'Approved').length,
                Math.max(1, allProcessLinks.length),
              ),
            },
            {
              dimension: 'Freshness',
              percent: ratio(
                allProcessLinks.filter((l) => l.provenance.qualityStatus !== 'Stale').length,
                Math.max(1, allProcessLinks.length),
              ),
            },
          ],
        },
        issues,
        recommendations: [
          {
            id: 'rec-1',
            iconToken: 'sparkle',
            text: `${unmapped.length} level 3 processes in scope have no product. Start with the ${unmapped[0]?.name ?? 'highest-volume'} step.`,
            actionRoute: ROUTES.mappingProcessProduct,
          },
          {
            id: 'rec-2',
            iconToken: 'merge',
            text: `${overlaps.length} processes have more than one primary product. Resolving these removes the largest source of double counting.`,
            actionRoute: ROUTES.mappingProcessProduct,
          },
          {
            id: 'rec-3',
            iconToken: 'shield',
            text: `${conflicts.length} source conflicts are waiting for a steward decision.`,
            actionRoute: ROUTES.adminCommandCenter,
          },
        ],
        issueTotals: {
          // The full population, against the capped list above. Both are needed:
          // the rail shows a workable queue, the header states how much of the
          // problem that queue is.
          unmapped: unmapped.length,
          overlap: overlaps.length,
          conflict: conflicts.length,
        },
        counters: {
          totalProcesses: l3Processes.length,
          mapped: mappedProcessCodes.size,
          unmapped: unmapped.length,
          overlaps: overlaps.length,
          conflicts: conflicts.length,
          averageConfidencePercent: round(averageConfidence * 100, 1),
        },
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.mappingProcessProduct,
          sources: PAGE_SOURCES.mapping,
          confidence: averageConfidence >= 0.8 ? 'High' : 'Medium',
          actions: [
            { action: 'proposeRelationship', permission: 'propose', enabled: canEdit(user) },
            {
              action: 'publishMappingSet',
              permission: 'approve',
              enabled: false,
              disabledReason: 'Publishing requires a data steward and a completed review.',
            },
            { action: 'export', permission: 'export', enabled: true },
          ],
        }),
      });
    },

    /**
     * Application-to-product mapping, from the fixture.
     *
     * Derived from the same process-mapping projection rather than built a
     * second time: the edges genuinely are the same records, and a second
     * derivation would drift from the first and be believed by whichever
     * screen read it. What differs is the question — this one is about
     * applications, so it keeps the application-touching edges and drops the
     * process tree the other view needs.
     *
     * `endpointRules` is empty here on purpose. The rules live in
     * `rel.endpoint_rule` and the fixture has no database to read them from;
     * an authored copy would be a second statement of the model that could
     * disagree with the enforced one. The studio treats an empty list as
     * "the server has not said", not as "nothing may be joined".
     */
    async getApplicationMapping(
      filters: GlobalFilters,
    ): Promise<QueryResult<ApplicationMappingView>> {
      const source = await this.getProcessMapping(filters);
      const view = source.data;
      const applicationIds = new Set(
        view.nodes.filter((node) => node.column === 'application').map((node) => node.id),
      );
      const edges = view.edges.filter(
        (edge) => applicationIds.has(edge.fromNodeId) || applicationIds.has(edge.toNodeId),
      );
      const endpointIds = new Set(edges.flatMap((edge) => [edge.fromNodeId, edge.toNodeId]));
      const nodes = view.nodes.filter(
        (node) => node.column !== 'process' && endpointIds.has(node.id),
      );
      const mapped = new Set(
        edges.map((edge) => (applicationIds.has(edge.toNodeId) ? edge.toNodeId : edge.fromNodeId)),
      );

      const payload: ApplicationMappingView = {
        mappingSet: view.mappingSet,
        nodes,
        edges,
        page: { returnedEdges: edges.length, totalEdges: edges.length, hasMore: false },
        counters: {
          totalApplications: applicationIds.size,
          mappedApplications: mapped.size,
          unmappedApplications: Math.max(0, applicationIds.size - mapped.size),
          productsReached: nodes.filter((node) => node.column === 'product').length,
          proposedEdges: edges.filter((edge) => edge.status === 'Proposed').length,
          approvedEdges: edges.filter((edge) => edge.status === 'Approved').length,
          supersededEdges: edges.filter((edge) => edge.status === 'Superseded').length,
        },
        endpointRules: [],
      };
      return resolved({ data: payload, meta: source.meta });
    },
  };
}
